import { isArray, isObject, isString } from 'lodash-es'
import type { DirectiveBinding } from 'vue'
import type { Handlers } from '~/resources/tracking'

export default defineNuxtPlugin({
  name: 'app:tracking',
  dependsOn: ['unleash:plugin'],
  setup(nuxtApp) {
    const isFreeweek = useFlag('freeweek')

    nuxtApp.vueApp.directive('track', {
      mounted(el, binding) {
        if (isFreeweek.value) return

        const [group] = binding.arg?.split(':', 2) || []

        if (group === 'impression') {
          setupTrackingImpressionObserver(el, binding, useTracking().handlers!)
        }
      },
    })
    nuxtApp.vueApp.directive('impression', {
      mounted(el, binding) {
        if (isFreeweek) return

        let threshold = Object.keys(binding.modifiers)
          .map((modifier) => parseInt(modifier))
          .filter((modifier) => typeof modifier === 'number')
          .map((modifier) => modifier / 100)
        if (!threshold.length) threshold = [0.5]

        let handler = ''
        if (binding.arg) {
          handler =
            binding.arg.split(':').length === 1
              ? `impression:${binding.arg}`
              : binding.arg
        }

        setupImpressionObserver(
          el,
          threshold,
          useTracking().handlers!,
          handler,
          binding.value,
        )
      },
    })
  },
})

function setupTrackingImpressionObserver(
  el: Element,
  binding: DirectiveBinding,
  handlers: Handlers,
) {
  if (!window.IntersectionObserver) {
    return
  }
  const thresholds = Object.keys(binding.modifiers)
    .map((modifier) => parseInt(modifier))
    .filter((modifier) => typeof modifier === 'number')
    .map((modifier) => modifier / 100)
  const options = { threshold: thresholds.length ? thresholds : [0.5] }

  const callback = (
    entries: IntersectionObserverEntry[],
    observer: IntersectionObserver,
  ) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        if (options.threshold.length === 1) {
          // If multiple thresholds are defined, we must not abort after one event.
          // If only one is defined, we abort, since subsequent events provide no added value here.
          observer.disconnect()
        }

        parseBinding(binding.value).forEach((eventBinding) =>
          callHandler(handlers, eventBinding, entry),
        )
      }
    })
  }

  const observer = new IntersectionObserver(callback, options)

  observer.observe(el)
}

type TrackingBinding = {
  event: string
  args?: unknown[]
  /** @deprecated Use `args` instead */
  params?: unknown
}

function parseBinding(binding: DirectiveBinding): TrackingBinding[] {
  if (isString(binding)) return [{ event: binding }]

  if (isArray(binding)) return binding as TrackingBinding[]

  if (isObject(binding) && 'event' in binding)
    return [binding as TrackingBinding]

  return Object.entries(binding).map(([event, args]) => ({ event, args }))
}

function callHandler(
  handlers: Handlers,
  binding: TrackingBinding,
  entry: IntersectionObserverEntry,
) {
  const { event, args, params } = binding
  const [category, name] = event.split(':')
  if (!handlers) {
    console.error(
      `Tracking handlers are not available when trying to access '${category}' category`,
    )
    return
  }
  const handlerCategory = handlers[category as keyof typeof handlers]
  const handler = handlerCategory[
    name as keyof typeof handlerCategory
  ] as Function
  handler?.call(null, ...(args ?? []), { params, entry })
}

function setupImpressionObserver(
  el: Element,
  threshold: number[],
  handlers: Handlers,
  handler: string,
  arg: any,
) {
  // console.log(' *** setting up impression observer ***', handler, threshold, arg)
  if (!window.IntersectionObserver) {
    return
  }
  const options = { threshold }

  const callback = (
    entries: IntersectionObserverEntry[],
    observer: IntersectionObserver,
  ) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        if (options.threshold.length === 1) {
          // If multiple thresholds are defined, we must not abort after one event.
          // If only one is defined, we abort, since subsequent events provide no added value here.
          observer.disconnect()
        }
        if (typeof arg === 'function') {
          arg({
            ratio: entry.intersectionRatio,
            threshold: observer.thresholds,
          })
        }
        if (handler) {
          const [category, name] = handler.split(':')
          const handlerCategory = handlers[category as keyof typeof handlers]
          // TODO: Was ist mit arg?
          ;(handlerCategory[name as keyof typeof handlerCategory] as Function)({
            data: arg,
            entry,
            thresholds: observer.thresholds,
          })
        }
      }
    })
  }

  const observer = new IntersectionObserver(callback, options)

  observer.observe(el)
}
